import { EventSubscribeable } from '@quickplay/foundation';
import { AdEvent, AdInfo, AdTrackingEvent } from '@quickplay/player-interface';
import { decodeCompanion, isHttpUrl } from './CompanionDecoder';
import { CompanionState, selectFormat } from './CompanionState';
import {
  CompanionAction,
  CompanionActionOutcome,
  CompanionActions,
  CompanionActionState,
  CompanionAdContext,
  CompanionConfiguration,
  CompanionEvent,
  CompanionFormat,
  CompanionSize,
  CustomCtaCreative,
} from './CompanionTypes';
import { createGptSlots, DEFAULT_NATIVE_SIZES, GptSlots } from './CompanionGptSlots';
import { CustomCtaView, resolveColors } from './CustomCtaView';
import { StaticCompanionController } from './StaticCompanionController';

const DEFAULT_IMAGE_TIMEOUT_MS = 8000;
const DEFAULT_ACTION_TIMEOUT_MS = 5000;
const CLICK_DEDUPE_MS = 500;

export interface CompanionPresentationOptions {
  /** Element that hosts the companion. The SDK only adds and removes its own child. */
  container: HTMLElement;
  /** Ad player (or any emitter) that publishes the `AdEvent` events. */
  adPlayer: EventSubscribeable;
  configuration?: CompanionConfiguration;
  actions?: CompanionActions;
  onEvent?: (event: CompanionEvent) => void;
}

export interface CompanionPresentation {
  /** Unsubscribes from the ad player and removes everything it added. Safe to call repeatedly. */
  release(): void;
  /** Reuses the same companion slots for another player. Undefined detaches and clears the current ad. */
  setAdPlayer(adPlayer: EventSubscribeable | undefined): void;
  /** Drops the current companion, e.g. when content changes. */
  clear(): void;
  /** Temporarily hide the presentation without clearing its creative or registered slots. */
  setVisible(visible: boolean): void;
  /** Set the current CTA label, disabled state, or visibility. Resets on the next ad. */
  setActionState(state: CompanionActionState): void;
}

function preload(url: string, timeoutMs: number): Promise<boolean> {
  return new Promise(resolve => {
    const img = new Image();
    const timer: { id?: ReturnType<typeof setTimeout> } = {};
    const finish = (ok: boolean): void => {
      clearTimeout(timer.id);
      img.onload = null;
      img.onerror = null;
      resolve(ok);
    };
    timer.id = setTimeout(() => finish(false), timeoutMs);
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
  });
}

function defaultOpen(url: string): boolean {
  if (!isHttpUrl(url)) {
    return false;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

export function createCompanionPresentation(options: CompanionPresentationOptions): CompanionPresentation {
  const { container, actions, onEvent } = options;
  let adPlayer: EventSubscribeable | undefined = options.adPlayer;
  const configuration = options.configuration ?? {};
  const formats: CompanionFormat[] = configuration.formats ?? ['customCta', 'standard'];
  const nativeSizes = configuration.nativeSizes ?? DEFAULT_NATIVE_SIZES;
  const imageTimeoutMs = configuration.imageTimeoutMs ?? DEFAULT_IMAGE_TIMEOUT_MS;
  const actionTimeoutMs = configuration.actionTimeoutMs ?? DEFAULT_ACTION_TIMEOUT_MS;
  const persistAfterAd = configuration.persistCompanionAfterAd ?? false;
  const state = new CompanionState();

  let released = false;
  let visible = true;
  let suppressStandard = false;
  let context: CompanionAdContext | undefined;
  let actionState: CompanionActionState = {};
  let standardWasShown = false;
  let view: CustomCtaView | undefined;
  // Epoch of the ad the mounted view belongs to; a new ad always gets a fresh view.
  let viewEpoch: number | undefined;
  let lastDecision = '';
  let retainedEmitted = false;
  let slots: GptSlots | undefined;
  let actionInFlight = false;
  let shownFormat: CompanionFormat | 'none' = 'none';
  // Fill revision of the standard companion kept after its ad ended; later fills have a higher revision
  // and are therefore not cleared along with it.
  let retainedFillRevision: number | undefined;
  // Highest fill revision when the retained ad completed. Cleanup clears through it, so unseen fills of
  // that ad go too while fills received afterwards survive.
  let completedFillRevision: number | undefined;
  // Fill revision of the GPT banner most recently made visible during the active ad.
  let displayedFillRevision: number | undefined;
  // Set by an explicit clear, skip, error or player change. Fills that arrive afterwards belong to the
  // invalidated ad, so they are rejected until a new ad or break starts. Fills before the first ad are kept.
  let rejectLateFills = false;
  // Identifies the latest `update` pass, so an outer pass can detect a nested one run by an app listener.
  let updateRun = 0;

  const emit = (event: CompanionEvent): void => {
    try {
      onEvent?.(context ? { ...event, context } : event);
    } catch {
      // Listener failures must not break presentation.
    }
  };

  const staticCompanion = new StaticCompanionController({
    root: container,
    sizes: nativeSizes,
    timeoutMs: imageTimeoutMs,
    onChanged: () => update(),
    onAction: runAction,
    onEvent: emit,
  });

  const removeView = (): void => {
    view?.dispose();
    view = undefined;
    viewEpoch = undefined;
  };

  /** Runs the app's handler, falling back to opening the URL. Repeated taps are ignored while one is in flight. */
  function runAction(url: string, onAccepted?: () => void): void {
    if (
      released ||
      !visible ||
      (state.phase !== 'adActive' && !staticCompanion.isRetained) ||
      actionInFlight ||
      (!onAccepted && actionState.disabled)
    ) {
      return;
    }
    actionInFlight = true;
    const epoch = state.epoch;
    const actionPhase = state.phase;
    const action: CompanionAction = { url, campaign: state.creative?.campaign };
    if (context) action.context = context;
    view?.setActionState({ ...actionState, disabled: true });
    let settled = false;
    const timer: { id?: ReturnType<typeof setTimeout> } = {};
    const settle = (outcome: CompanionActionOutcome): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer.id);
      // Keep rejecting repeated taps briefly after the action settles.
      setTimeout(() => {
        if (epoch === state.epoch) {
          actionInFlight = false;
          view?.setActionState(actionState);
        }
      }, CLICK_DEDUPE_MS);
      // The result of an action from an earlier or completed ad is no longer meaningful.
      if (released || epoch !== state.epoch || state.phase !== actionPhase) {
        return;
      }
      emit({ type: 'ActionResult', action, outcome });
    };
    timer.id = setTimeout(() => settle('timedOut'), actionTimeoutMs);

    const fallback = (): void => {
      if (settled || released || epoch !== state.epoch) {
        return;
      }
      if (!visible || state.phase !== actionPhase || (!onAccepted && actionState.hidden)) {
        settle('blocked');
        return;
      }
      try {
        settle(defaultOpen(url) ? 'opened' : 'blocked');
      } catch {
        settle('failed');
      }
    };

    onAccepted?.();
    if (released || epoch !== state.epoch) return;
    // The accepted-tap listener may have hidden the presentation or ended the ad.
    if (!visible || state.phase !== actionPhase) {
      settle('blocked');
      return;
    }
    if (!actions?.onAction) {
      fallback();
      return;
    }
    try {
      Promise.resolve(
        actions.onAction(action, nextState => {
          if (!settled && !released && epoch === state.epoch && state.phase === actionPhase) {
            actionState = { ...actionState, ...nextState };
            update();
          }
        }),
      ).then(
        handled => (handled ? settle('handled') : fallback()),
        () => settle('failed'),
      );
    } catch {
      settle('failed');
    }
  }

  const buildView = (creative: CustomCtaCreative): CustomCtaView => {
    const colors = resolveColors(configuration.theme ?? {}, {
      surface: creative.surfaceColor,
      text: creative.textColor,
      button: creative.ctaBgColor,
      buttonText: creative.ctaTextColor,
    });
    const epoch = state.epoch;
    return new CustomCtaView(
      creative,
      colors,
      () => runAction(creative.ctaUrl),
      url => {
        if (released || epoch !== state.epoch || state.phase !== 'adActive') return;
        state.setAssets('failed');
        update();
        if (!released && epoch === state.epoch) emit({ type: 'AssetFailed', url, campaign: creative.campaign });
      },
    );
  };

  /** Mounts or removes the in-house view so it matches the selected format. */
  const syncView = (shown: CompanionFormat | 'none'): void => {
    const wantedEpoch = shown === 'customCta' ? state.epoch : undefined;
    if (wantedEpoch !== viewEpoch) {
      removeView();
      if (wantedEpoch !== undefined && state.creative) {
        view = buildView(state.creative);
        viewEpoch = wantedEpoch;
        container.append(view.element);
      }
    }
    if (view) {
      view.element.style.display = visible ? 'flex' : 'none';
      view.setActionState({ ...actionState, disabled: actionInFlight || actionState.disabled });
    }
  };

  /** Reports what is on screen, once per ad and format; nothing is reported while the icon is still loading. */
  const emitDecision = (effective: CompanionFormat | 'none', size?: CompanionSize, reason?: string): void => {
    const decision = `${state.epoch}:${effective}`;
    const settled = effective !== 'none' || state.assets !== 'loading';
    if (decision === lastDecision || state.phase === 'idle' || !settled) {
      return;
    }
    lastDecision = decision;
    const noneReason = reason ?? (state.assets === 'failed' ? 'asset_failed' : 'not_eligible');
    emit({
      type: 'DisplayDecision',
      shown: effective,
      reason: effective === 'none' ? noneReason : undefined,
      campaign: state.creative?.campaign,
      size,
    });
  };

  /** A retained standard companion counts as retained once content has resumed underneath it. */
  const emitRetainedOnce = (effective: CompanionFormat | 'none'): void => {
    const retained =
      (retainedFillRevision !== undefined || staticCompanion.isRetained) && state.resumed && effective === 'standard';
    if (!retainedEmitted && retained) {
      retainedEmitted = true;
      emit({ type: 'Retained', campaign: state.creative?.campaign });
    }
  };

  /** Projects the current ad, assets and fill state into one eligible format. */
  function selectCurrentFormat(fillRevision?: number): CompanionFormat | 'none' {
    if (suppressStandard) return 'none';
    return selectFormat({
      phase: state.phase,
      creative: actionState.hidden ? undefined : state.creative,
      assets: state.assets,
      formats,
      standardFilled: staticCompanion.ready || (!!slots && slots.filled(fillRevision).length > 0),
      standardRetained: staticCompanion.isRetained || retainedFillRevision !== undefined,
    });
  }

  /** Re-evaluates which format owns the region and brings the DOM and events in line with it. */
  function update(reason?: string): void {
    if (released) {
      return;
    }
    const epoch = state.epoch;
    const run = ++updateRun;
    const fillRevision = state.phase === 'adEnded' ? retainedFillRevision : undefined;
    const shown = selectCurrentFormat(fillRevision);
    syncView(shown);
    const wantsStandard = visible && shown === 'standard';
    const staticSize = staticCompanion.show(wantsStandard);
    // A tracking listener may have restarted the ad or run a nested update; that pass owns the result.
    if (released || epoch !== state.epoch || run !== updateRun) return;
    const gptSize = slots?.show(wantsStandard && !staticCompanion.ready, container.clientWidth, fillRevision);
    if (gptSize) displayedFillRevision = slots?.shownRevision();
    const standardSize = staticSize ?? gptSize;
    const viewFormat = view ? shown : 'none';
    const rendered = standardSize ? 'standard' : viewFormat;
    const effective = visible ? rendered : 'none';
    shownFormat = shown;
    if (standardSize) standardWasShown = true;
    emitDecision(effective, standardSize, visible ? reason : 'presentation_hidden');
    emitRetainedOnce(effective);
  }

  /** Drops everything belonging to the current ad. Pass `clearSlots = false` to keep a fresh standard fill. */
  const reset = (clearSlots = true): void => {
    state.clear();
    staticCompanion.clear();
    context = undefined;
    suppressStandard = false;
    actionState = {};
    actionInFlight = false;
    standardWasShown = false;
    retainedFillRevision = undefined;
    completedFillRevision = undefined;
    displayedFillRevision = undefined;
    shownFormat = 'none';
    if (clearSlots) {
      slots?.clear();
    }
    retainedEmitted = false;
    lastDecision = '';
    removeView();
  };

  /** Drops the current ad because the app, the player or the provider invalidated it. */
  const invalidate = (): void => {
    reset();
    rejectLateFills = true;
  };

  /** Clears the retained standard fill, leaving any newer fill that already arrived. */
  const dropRetainedStandard = (): void => {
    if (retainedFillRevision !== undefined) {
      slots?.clear(completedFillRevision ?? retainedFillRevision);
    }
  };

  const loadIcon = async (epoch: number, creative: CustomCtaCreative): Promise<void> => {
    const loaded = await preload(creative.iconUrl, imageTimeoutMs);
    // The ad may have ended or changed while the icon was loading.
    if (released || epoch !== state.epoch || state.phase !== 'adActive') {
      return;
    }
    if (!loaded) {
      emit({ type: 'AssetFailed', url: creative.iconUrl, campaign: creative.campaign });
    }
    if (released || epoch !== state.epoch || state.phase !== 'adActive') return;
    state.setAssets(loaded ? 'ready' : 'failed');
    update();
  };

  const onAdStart = (info?: AdInfo): void => {
    if (released) {
      return;
    }
    const {
      creative,
      reason,
      suppressStandard: suppressed,
    } = (configuration.decode ?? decodeCompanion)(info?.traffickingParameters);
    rejectLateFills = false;
    // Preserve a new fill that arrived before adstart, while discarding the previous retained creative.
    // An ad still active here never ended, so any fill in the slots belongs to it.
    const replacingActiveAd = state.phase === 'adActive';
    dropRetainedStandard();
    reset(replacingActiveAd);
    suppressStandard = suppressed ?? false;
    if (info) {
      context = {
        adId: info.adID,
        creativeId: info.vastProperties?.creativeId,
        adBreakId: info.adBreakInfo?.adBreakID,
        adSessionId: info.adSessionId,
        delivery: info.delivery,
      };
    }
    if (context && !Object.values(context).some(value => value !== undefined)) context = undefined;
    const epoch = state.start(creative);
    if (suppressStandard) slots?.clear();
    else if (formats.includes('standard')) staticCompanion.start(info?.companionAds);
    if (!creative) {
      state.setAssets('failed');
      // Missing in-house parameters do not tell us whether GPT will fill a standard companion.
      update(reason ?? (slots ? 'awaiting_standard_companion' : 'no_companion'));
      return;
    }
    update();
    void loadIcon(epoch, creative);
  };

  const onAdEnd = (): void => {
    if (state.phase === 'adActive') {
      retainedFillRevision =
        persistAfterAd && shownFormat === 'standard' && standardWasShown && !staticCompanion.ready
          ? displayedFillRevision
          : undefined;
      completedFillRevision = retainedFillRevision === undefined ? undefined : slots?.revision();
      staticCompanion.end(configuration.persistStaticCompanionAfterAd ?? false);
      state.end();
      if (retainedFillRevision === undefined) {
        slots?.clear();
      }
    }
    update();
  };

  const onClear = (): void => invalidate();
  const onTrackingEvent = (event: AdTrackingEvent): void => {
    if (event === AdTrackingEvent.AD_SKIPPED_EVENT) invalidate();
  };

  const onBreakEnd = (): void => {
    const epoch = state.epoch;
    onAdEnd();
    // A DisplayDecision listener may have started another ad or released the presentation.
    if (released || epoch !== state.epoch) return;
    state.resumeContent();
    if (retainedFillRevision === undefined) slots?.clear();
    update();
  };

  const onBreakStart = (): void => {
    rejectLateFills = false;
    if (state.phase === 'adEnded') {
      dropRetainedStandard();
      reset(false);
    }
    update();
  };

  const onResize = (): void => {
    staticCompanion.resize();
    update();
  };

  const onSlotFill = (): void => {
    if (rejectLateFills) {
      slots?.clear();
      return;
    }
    // Between ads, preserve upcoming fills but keep them hidden until adstart.
    // Once content resumes, unretained late fills cannot belong to the active break.
    if (state.phase === 'adEnded' && state.resumed && retainedFillRevision === undefined) {
      slots?.clear();
      return;
    }
    update();
  };

  const native = configuration.native;
  if (formats.includes('standard') && native?.adUnitPath) {
    const googletag = native.googletag ?? (window as any).googletag;
    if (googletag?.cmd && typeof googletag.cmd.push === 'function') {
      slots = createGptSlots({
        root: container,
        adUnitPath: native.adUnitPath,
        googletag,
        sizes: nativeSizes,
        onFillChanged: onSlotFill,
      });
    } else {
      emit({ type: 'CapabilityLimited', reason: 'googletag_missing' });
    }
  }

  const subscriptions: [string, NonNullable<Parameters<EventSubscribeable['subscribe']>[1]>][] = [
    [AdEvent.ADVERT_START, onAdStart],
    [AdEvent.ADVERT_END, onAdEnd],
    [AdEvent.ADVERT_SKIP, onClear],
    [AdEvent.ADVERT_TRACKING_EVENT, onTrackingEvent],
    [AdEvent.ADVERT_ERROR, onClear],
    [AdEvent.ADVERT_BREAK_END, onBreakEnd],
    [AdEvent.ADVERT_BREAK_START, onBreakStart],
  ];
  subscriptions.forEach(([name, callback]) => adPlayer?.subscribe(name, callback));
  window.addEventListener('resize', onResize);

  return {
    setVisible: nextVisible => {
      if (released || nextVisible === visible) return;
      visible = nextVisible;
      if (visible) staticCompanion.resize();
      update();
    },
    setActionState: nextState => {
      if (released || state.phase !== 'adActive') return;
      actionState = { ...actionState, ...nextState };
      update();
    },
    setAdPlayer: nextPlayer => {
      if (released || nextPlayer === adPlayer) {
        return;
      }
      subscriptions.forEach(([name, callback]) => adPlayer?.unsubscribe(name, callback));
      invalidate();
      adPlayer = nextPlayer;
      subscriptions.forEach(([name, callback]) => adPlayer?.subscribe(name, callback));
    },
    clear: () => {
      if (!released) {
        invalidate();
      }
    },
    release: () => {
      if (released) {
        return;
      }
      released = true;
      subscriptions.forEach(([name, callback]) => adPlayer?.unsubscribe(name, callback));
      window.removeEventListener('resize', onResize);
      state.clear();
      staticCompanion.clear();
      removeView();
      slots?.destroy();
      slots = undefined;
    },
  };
}
